"use client";

import React from "react";
import { SipCalculator } from "./SipCalculator";
import {
  DEFAULT_INVESTMENT_SLIDER_MARKERS,
  DEFAULT_INTEREST_RATE_SLIDER_MARKERS,
  DEFAULT_TENURE_SLIDER_MARKERS,
} from "./AMCSipCalculatorConstants";
import { mapContentToSipCalculatorProps } from "./AMCSipCalculatorUtils";
import type {
  AMCSipCalculatorProps,
  AMCSipCalculatorSipCalculatorProps,
} from "./AMCSipCalculator.interface";

export type { AMCSipCalculatorProps } from "./AMCSipCalculator.interface";

/**
 * SIP calculator for AMC PDP right column (above RTE / About sections).
 * When `content` is provided, slider bounds, defaults, and labels are read from AEM.
 */
export const AMCSipCalculator: React.FC<AMCSipCalculatorProps> = ({
  className = "",
  sectionId = "sip-calculator",
  content,
  title = "Calculate your return",
  defaultInvestmentType = "sip",
  initialInvestment = 25000,
  initialTenureYears = 10,
  initialInterestRate = 12,
  ctaLabel = "EXPLORE",
  ctaUrl = "/investments/funds",
  investmentSliderMarkers = [...DEFAULT_INVESTMENT_SLIDER_MARKERS],
  tenureSliderMarkers = [...DEFAULT_TENURE_SLIDER_MARKERS],
  interestRateSliderMarkers = [...DEFAULT_INTEREST_RATE_SLIDER_MARKERS],
  ...sipCalculatorProps
}) => {
  const cmsProps = React.useMemo(
    () => (content ? mapContentToSipCalculatorProps(content) : {}),
    [content]
  );

  const calculatorProps: AMCSipCalculatorSipCalculatorProps = {
    ...cmsProps,
    title: cmsProps.title ?? title,
    defaultInvestmentType: cmsProps.defaultInvestmentType ?? defaultInvestmentType,
    initialInvestment: cmsProps.initialInvestment ?? initialInvestment,
    initialTenureYears: cmsProps.initialTenureYears ?? initialTenureYears,
    initialInterestRate: cmsProps.initialInterestRate ?? initialInterestRate,
    investmentSliderMarkers: cmsProps.investmentSliderMarkers ?? investmentSliderMarkers,
    tenureSliderMarkers: cmsProps.tenureSliderMarkers ?? tenureSliderMarkers,
    interestRateSliderMarkers: cmsProps.interestRateSliderMarkers ?? interestRateSliderMarkers,
    ctaLabel,
    ctaUrl,
    ...sipCalculatorProps,
  };
  const calculatorSchema = {
    "@context": "https://schema.org",
    "@type": "FinancialProduct",
    name: calculatorProps.title ?? title,
    category: "Mutual Fund",
    description: "SIP and lumpsum return calculator for mutual fund investments.",
    provider: {
      "@type": "Organization",
      name: "Bajaj Finserv",
    },
    ...(ctaUrl
      ? {
          offers: {
            "@type": "Offer",
            name: ctaLabel,
            url: ctaUrl,
          },
        }
      : {}),
    additionalProperty: [
      {
        "@type": "PropertyValue",
        name: "defaultInvestmentType",
        value: calculatorProps.defaultInvestmentType,
      },
      {
        "@type": "PropertyValue",
        name: "initialInvestment",
        value: calculatorProps.initialInvestment,
      },
      {
        "@type": "PropertyValue",
        name: "initialTenureYears",
        value: calculatorProps.initialTenureYears,
      },
      {
        "@type": "PropertyValue",
        name: "initialInterestRate",
        value: calculatorProps.initialInterestRate,
      },
    ],
  };

  const sipCalculatorTitle = calculatorProps.title ?? title;

  return (
    <section
      id={sectionId}
      className={`mb-6 w-full md:mb-10 ${className}`.trim()}
      data-cmp="AMCSipCalculator"
      data-cmp-type="react"
      data-cmp-title={sipCalculatorTitle}
    >
      <style>{`
        /* Fix for Tailwind arbitrary breakpoints not being compiled */
        [data-cmp="SipCalculator"] > .min-\\[900px\\]\\:hidden {
          display: block;
        }
        [data-cmp="SipCalculator"] > .hidden.min-\\[900px\\]\\:grid {
          display: none;
        }
        @media (min-width: 900px) {
          [data-cmp="SipCalculator"] > .min-\\[900px\\]\\:hidden {
            display: none;
          }
          [data-cmp="SipCalculator"] > .hidden.min-\\[900px\\]\\:grid {
            display: grid;
            grid-template-columns: repeat(1, minmax(0, 1fr));
            align-items: start;
            gap: 16px;
          }
        }
        @media (min-width: 1261px) {
          [data-cmp="SipCalculator"] > .hidden.min-\\[900px\\]\\:grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          [data-cmp="SipCalculator"] .bfl-sip-calc-desktop-col {
            height: 600px;
          }
        }
        @media (min-width: 1400px) {
          [data-cmp="SipCalculator"] .bfl-sip-calc-desktop-col {
            height: 640px;
          }
        }
        
        [data-cmp="SipCalculatorDonutChart"] > div > div:first-child {
          width: auto !important;
          flex: 0 0 auto !important;
        }
        [data-cmp="SipCalculatorDonutChart"] .bfl-sip-donut-ring,
        [data-cmp="SipCalculatorDonutChart"] .relative.rounded-full {
          width: 150px !important;
          height: 150px !important;
          min-width: 150px !important;
          min-height: 150px !important;
          max-width: 150px !important;
          max-height: 150px !important;
          aspect-ratio: 1 / 1 !important;
          flex-shrink: 0 !important;
        }
        @media (min-width: 900px) {
          [data-cmp="SipCalculatorDonutChart"] .bfl-sip-donut-ring,
          [data-cmp="SipCalculatorDonutChart"] .relative.rounded-full {
            width: 204px !important;
            height: 204px !important;
            min-width: 204px !important;
            min-height: 204px !important;
            max-width: 204px !important;
            max-height: 204px !important;
          }
        }
      `}</style>
      <SipCalculator {...calculatorProps} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(calculatorSchema) }}
      />
    </section>
  );
};

export default AMCSipCalculator;
